import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { companyAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { Loader2, ArrowLeft } from 'lucide-react';

const inp = "w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-gray-50";

export default function CompanyRegister() {
  const navigate = useNavigate();
  const { companyLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    organizationType: 'COMPANY',
    companyName: '', contactPersonName: '',
    contactEmail: '', contactPhone: '',
    gstNumber: '', panNumber: '',
    address: '', city: '', state: '', pincode: '',
    password: '', confirmPassword: '',
  });

  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const { confirmPassword, ...payload } = form;
      // Register
      await companyAPI.register(payload);
      // Auto-login immediately after registration
      const loginRes = await companyAPI.login(payload.contactPhone, payload.password);
      companyLogin(loginRes.data.data);
      toast.success('Registration submitted! Your account is pending admin approval.');
      navigate('/company/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const orgLabel = form.organizationType === 'HOSPITAL' ? 'Hospital' : 'Company';

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600 text-sm mb-6 transition">
          <ArrowLeft className="h-4 w-4" /> Back to Home
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <div className="mb-8">
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-9 w-auto mb-6" />
            <p className="text-teal-600 text-sm font-semibold uppercase tracking-widest mb-1">Organization Portal</p>
            <h1 className="text-2xl font-bold text-slate-900">Register Your Organization</h1>
            <p className="text-gray-400 text-sm mt-1">Join ShramSena's trusted network of healthcare employers</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-7">

            {/* Organization Type */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">Organization Type *</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'COMPANY',  label: 'Healthcare Company', desc: 'Staffing agency, care home, clinic' },
                  { value: 'HOSPITAL', label: 'Hospital',           desc: 'Government or private hospital' },
                ].map((opt) => (
                  <button key={opt.value} type="button"
                    onClick={() => setForm({ ...form, organizationType: opt.value })}
                    className={`text-left p-4 rounded-xl border-2 transition
                      ${form.organizationType === opt.value
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300'}`}>
                    <p className={`font-semibold text-sm ${form.organizationType === opt.value ? 'text-teal-700' : 'text-slate-700'}`}>
                      {opt.label}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Organization Info */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">{orgLabel} Information</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-semibold text-slate-700 mb-1">{orgLabel} Name *</label>
                  <input name="companyName" value={form.companyName} onChange={set} required
                    placeholder={form.organizationType === 'HOSPITAL' ? 'City General Hospital' : 'Acme Healthcare Pvt Ltd'}
                    className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Contact Person *</label>
                  <input name="contactPersonName" value={form.contactPersonName} onChange={set} required
                    placeholder="Full Name" className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">GST Number</label>
                  <input name="gstNumber" value={form.gstNumber} onChange={set}
                    placeholder="22AAAAA0000A1Z5" className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">PAN Number</label>
                  <input name="panNumber" value={form.panNumber} onChange={set}
                    placeholder="ABCDE1234F" maxLength={10} className={inp} />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Contact Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Phone Number *</label>
                  <input name="contactPhone" value={form.contactPhone} onChange={set} required
                    placeholder="9876543210" maxLength={10} className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Email Address *</label>
                  <input name="contactEmail" type="email" value={form.contactEmail} onChange={set} required
                    placeholder="hr@organization.com" className={inp} />
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Address</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <input name="address" value={form.address} onChange={set}
                    placeholder="Street address" className={inp} />
                </div>
                <div>
                  <input name="city" value={form.city} onChange={set} placeholder="City" className={inp} />
                </div>
                <div>
                  <input name="state" value={form.state} onChange={set} placeholder="State" className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Pincode</label>
                  <input name="pincode" value={form.pincode} onChange={set}
                    placeholder="400001" maxLength={6} className={inp} />
                </div>
              </div>
            </div>

            {/* Password */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Account Password</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Password *</label>
                  <input name="password" type="password" value={form.password} onChange={set} required
                    placeholder="Min 8 characters" className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Confirm Password *</label>
                  <input name="confirmPassword" type="password" value={form.confirmPassword} onChange={set} required
                    placeholder="Re-enter password" className={inp} />
                </div>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-3.5 rounded-xl
                font-bold text-sm hover:opacity-90 transition shadow-lg shadow-teal-500/20
                flex items-center justify-center gap-2 disabled:opacity-60">
              {loading
                ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</>
                : 'Register Organization'}
            </button>

            <p className="text-center text-sm text-gray-400">
              Already registered?{' '}
              <Link to="/company/login" className="text-teal-600 font-semibold hover:underline">
                Sign in here
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}